import React, {useState} from 'react';

import {Owner, SchemaCollaborator} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import {ClientTagSchemaConstraint, RegistryLabelingStrategy, SavingState} from 'constants/RegistryConstants';
import {
  TagSchemaItemType,
  DefaultSchemaCollaboratorsConfigurationSchemaType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as LabelingStrategiesUtil from 'registry/labeling-strategies/LabelingStrategiesUtil';
import {isValidSchemaInterfacePolicy} from 'schemas/schema-interface/SchemaInterfacePermissionSettings';
import {schemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';
import SchemaWithOrgPermissionsCreateFlow from 'schemas/SchemaWithOrgPermissionsCreateFlow';
import {itemTypeToFolderItemTypeAndSequenceType} from 'schemas/TagSchemaItemTypeUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {capwords} from 'util/NameUtil';
import {withLoadingScreen} from 'util/Promises';

import {SchemaInterfaceEditorQuery} from './__generated/SchemaInterfaceEditorQuery';
import SchemaInterfaceCreateFieldTable from './SchemaInterfaceCreateFieldTable';
import {
  convertToSchemaConstraintCreatePayload,
  convertToSchemaInterfaceFieldForRequest,
  createSchemaInterface,
  schemaInterfaceURL,
} from './SchemaInterfaceDAO';
import SchemaInterfaceMetadataSettings from './SchemaInterfaceMetadataSettings';
import {ClientInterfaceField} from './SchemaInterfaceUtils';

type Organization = NonNullable<SchemaInterfaceEditorQuery['currentTenantOrganizations']>[number];

export interface SchemaInterfaceCreateFlowProps {
  ownerChoices: Array<Owner>;
  organizations: Array<Organization>;
}

export default function SchemaInterfaceCreateFlow(props: SchemaInterfaceCreateFlowProps): JSX.Element {
  const [name, setName] = useState('');
  const [ownerId, setOwnerId] = useState(props.ownerChoices[0]?.id);
  const [sqlIdentifier, setSqlIdentifier] = useState<string | null>(null);
  const [fields, setFields] = useState<Array<ClientInterfaceField>>([]);
  const [itemType, setItemType] = useState<TagSchemaItemType | null>(null);
  const [constraint, setConstraint] = useState<ClientTagSchemaConstraint | null>(null);
  const [prefix, setPrefix] = useState<string>('');
  const [labelingStrategies, setLabelingStrategies] = useState<Array<RegistryLabelingStrategy>>([]);
  const [enforceNamingStrategies, setEnforceLabelingStrategies] = useState<boolean>(false);

  function createInterface(collaborators?: Array<SchemaCollaborator>): void {
    withLoadingScreen(
      (async () => {
        const createResponse = await createSchemaInterface({
          name,
          organizationId: ownerId,
          sqlIdentifier:
            BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME && sqlIdentifier ? sqlIdentifier : null,
          itemType,
          constraint: constraint && convertToSchemaConstraintCreatePayload(constraint),
          prefix,
          labelingStrategies,
          collaborators,
          fields: fields.map((field) => convertToSchemaInterfaceFieldForRequest(field)),
        });
        BenchlingUtil.navigate(null, schemaInterfaceURL(createResponse.id));
      })(),
      'Creating schema interface...'
    );
  }

  const handleEnforceLabelingStrategiesChange = (enableNamingStrategy: boolean): void => {
    setEnforceLabelingStrategies(enableNamingStrategy);
    if (enableNamingStrategy === false) {
      setLabelingStrategies([]);
    } else {
      const initialLabelingStrategies = LabelingStrategiesUtil.getInitialLabelingStrategies();
      setLabelingStrategies(initialLabelingStrategies);
    }
  };

  function renderMetadataSettings(handleNext: () => void): JSX.Element {
    return (
      <SchemaInterfaceMetadataSettings
        interface={null}
        name={name}
        setName={setName}
        ownerId={ownerId}
        setOwnerId={setOwnerId}
        sqlIdentifier={sqlIdentifier}
        setSqlIdentifier={setSqlIdentifier}
        ownerChoices={props.ownerChoices}
        itemType={itemType}
        setItemType={setItemType}
        prefix={prefix}
        setPrefix={setPrefix}
        enforceLabelingStrategies={enforceNamingStrategies}
        setEnforceLabelingStrategies={handleEnforceLabelingStrategiesChange}
        labelingStrategies={labelingStrategies}
        setLabelingStrategies={setLabelingStrategies}
        nameTemplateParts={[]}
        constraint={constraint}
        setConstraint={setConstraint}
        handleNext={handleNext}
        fields={fields}
        fieldTable={
          <SchemaInterfaceCreateFieldTable
            ownerId={ownerId}
            ownerUrlId=""
            isDisabled={false}
            fields={fields}
            savingState={SavingState.COMPLETED}
            onFieldsChange={setFields}
            baseBioentityType={itemType ? itemTypeToFolderItemTypeAndSequenceType(itemType) : null}
            schemaId={null}
          />
        }
      />
    );
  }

  return (
    <div className="u-flex-grow" {...getTestProps({component: 'SchemaInterfaceCreateFlow', element: 'root'})}>
      <SchemaWithOrgPermissionsCreateFlow
        createSchema={createInterface}
        defaultSchemaCollaboratorsSchemaType={DefaultSchemaCollaboratorsConfigurationSchemaType.INTERFACE}
        metadataTitle={`${capwords(schemaFieldsetName())} Settings`}
        isSchemaPermissionsEnabled={true}
        ownerId={ownerId}
        organizations={props.organizations}
        policyPredicate={isValidSchemaInterfacePolicy}
        renderMetadataSettings={renderMetadataSettings}
      />
    </div>
  );
}
